/**
 * Historic threads + AI customer summary — skeleton API (dev) or live human-agent API.
 */
import { getConversationGraph } from "@/api/conversationsApi";
import { ARCHIVED_THREAD_STUBS } from "@/mocks/customerHistory";

/**
 * Map GET .../customer-history (camelCase) to UI state for the right rail.
 * @param {Record<string, unknown>} body
 * @returns {{ historicThreads: unknown[], aiSummary: Record<string, unknown> }}
 */
export function mapInboxCustomerHistoryToUiPayload(body) {
  if (!body || typeof body !== "object") {
    return { historicThreads: [], aiSummary: null };
  }
  const b = /** @type {Record<string, unknown>} */ (body);
  const related = /** @type {unknown[]} */ (b.relatedThreads ?? b.related_threads ?? []);
  const ai = /** @type {Record<string, unknown>} */ (b.ai && typeof b.ai === "object" ? b.ai : {});

  const historicThreads = related.map((row) => {
    const t = /** @type {Record<string, unknown>} */ (row && typeof row === "object" ? row : {});
    return {
      id: String(t.id ?? ""),
      navigable: t.navigable !== false,
      startedAtLabel: String(t.startedAtLabel ?? t.started_at_label ?? "—"),
      preview: String(t.preview ?? ""),
      status: String(t.status ?? "open"),
      messageCount: typeof t.messageCount === "number" ? t.messageCount : 0,
      matchReasons: Array.isArray(t.matchReasons) ? t.matchReasons : [],
      workspaceId: t.workspaceId != null ? String(t.workspaceId) : t.workspace_id != null ? String(t.workspace_id) : null,
      workspaceName:
        t.workspaceName != null
          ? String(t.workspaceName)
          : t.workspace_name != null
            ? String(t.workspace_name)
            : null,
    };
  });

  const ev = Array.isArray(ai.evidence) ? ai.evidence : [];
  const evidence = ev
    .map((e) => {
      const x = /** @type {Record<string, unknown>} */ (e && typeof e === "object" ? e : {});
      return {
        text: String(x.text ?? ""),
        sessionId: String(x.sessionId ?? x.session_id ?? ""),
        sentAt: x.sentAt != null || x.sent_at != null ? String(x.sentAt ?? x.sent_at) : null,
      };
    })
    .filter((x) => x.text);

  const aiSummary = {
    summary: String(ai.summary ?? ""),
    highlights: Array.isArray(ai.highlights) ? ai.highlights : [],
    evidence,
    disclaimer: String(ai.disclaimer ?? ""),
    generatedAt: String(ai.generatedAt ?? ai.generated_at ?? new Date().toISOString()),
    source: String(ai.source ?? ""),
    error: ai.error != null && ai.error !== "" ? String(ai.error) : null,
  };

  return { historicThreads, aiSummary };
}

const stubDelay = (ms = 90) => new Promise((r) => setTimeout(r, ms));

function collectEmails(conv) {
  if (!conv) return [];
  return [...new Set([conv.user?.email, ...(conv.identityEmails || [])].filter(Boolean))];
}

function matchReasonsBetween(a, b) {
  const reasons = [];
  if (a.visitorSessionId && b.visitorSessionId && a.visitorSessionId === b.visitorSessionId) {
    reasons.push("device");
  }
  const ae = collectEmails(a);
  const be = collectEmails(b);
  if (ae.some((e) => be.includes(e))) reasons.push("email");
  return reasons;
}

function stubRowFromConversation(other, reasons) {
  return {
    id: other.id,
    navigable: true,
    startedAtLabel: other.timestamp || "—",
    preview: other.preview,
    status: other.status,
    messageCount: other.messages?.length ?? 0,
    matchReasons: reasons,
  };
}

function stubRowFromArchived(arch, reasons) {
  return {
    id: arch.id,
    navigable: arch.navigable !== false,
    startedAtLabel: arch.startedAtLabel,
    preview: arch.preview,
    status: arch.status,
    messageCount: arch.messageCount,
    matchReasons: reasons,
  };
}

/** @param {unknown[]} allConversations @param {string} conversationId */
function buildHistoricRows(allConversations, conversationId) {
  const current = allConversations.find((c) => c.id === conversationId);
  if (!current) return [];

  const rows = [];

  for (const other of allConversations) {
    if (other.id === current.id) continue;
    const reasons = matchReasonsBetween(current, other);
    if (reasons.length) rows.push(stubRowFromConversation(other, reasons));
  }

  for (const arch of ARCHIVED_THREAD_STUBS) {
    const reasons = [];
    if (current.visitorSessionId && arch.visitorSessionId === current.visitorSessionId) reasons.push("device");
    const curEmails = collectEmails(current);
    if (arch.identityEmails?.some((e) => curEmails.includes(e))) reasons.push("email");
    if (reasons.length) rows.push(stubRowFromArchived(arch, [...new Set(reasons)]));
  }

  rows.sort((a, b) => String(a.startedAtLabel).localeCompare(String(b.startedAtLabel)));
  return rows;
}

/**
 * @param {{ conversationId: string }} params
 */
export async function getHistoricThreads(params) {
  await stubDelay();
  const { data: all } = await getConversationGraph();
  return { data: buildHistoricRows(all ?? [], params.conversationId) };
}

/**
 * @param {{ conversationId: string }} params
 */
export async function getCustomerHistoryAiSummary(params) {
  await stubDelay(120);
  const { data: all } = await getConversationGraph();
  const current = (all ?? []).find((c) => c.id === params.conversationId);
  if (!current) {
    return {
      data: {
        summary: "No customer context available for this conversation.",
        highlights: [],
        evidence: [],
        generatedAt: new Date().toISOString(),
        source: "skeleton",
      },
    };
  }

  const historic = buildHistoricRows(all ?? [], params.conversationId);
  const threadCount = historic.length + 1;
  const themes = [
    ...new Set(
      [current.preview, ...historic.map((h) => h.preview)].filter(Boolean).map((p) => String(p).slice(0, 80))
    ),
  ].slice(0, 4);

  const summary = `Across ${threadCount} linked conversation${threadCount === 1 ? "" : "s"}, this visitor has mostly reached out about billing and account changes. Recent activity shows duplicate charges and plan adjustments; earlier threads reference proration and tax documentation. Tone in prior messages is cooperative and detail-oriented.`;

  const highlights = [
    `${threadCount} thread${threadCount === 1 ? "" : "s"} linked by same device session and/or shared email addresses on file.`,
    themes[0] ? `Latest topic: ${themes[0]}` : "Limited transcript context in skeleton mode.",
    "Verify any financial actions against your billing system before confirming with the customer.",
  ];

  return {
    data: {
      summary,
      highlights,
      evidence: [],
      generatedAt: new Date().toISOString(),
      source: "skeleton-ai-placeholder",
      disclaimer: "AI-generated from linked historic conversations. Not verified — confirm facts in source threads.",
    },
  };
}
